<template>
    <div v-if="visible" class="sidebar-overlay" @click="close">
        <div class="sidebar" @click.stop>
            <div class="sidebar-header">
                <h3 class="sidebar-title">Подключение канала</h3>
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <div class="sidebar-content">
                <div v-if="loading" class="loading-state">
                    <SpinnerLoader />
                    <p class="mt-4">Загрузка данных подключения...</p>
                </div>
                <div v-else-if="connectionError" class="error-state">
                    <v-icon color="error" size="48">mdi-alert-circle</v-icon>
                    <p class="mt-4">{{ connectionError }}</p>
                </div>
                <div v-else-if="needsUpgrade" class="upgrade-state">
                    <v-icon color="warning" size="48">mdi-alert</v-icon>
                    <h4 class="mt-4">Необходимо докупить канал</h4>
                    <p class="mt-2">Лимит бесплатных каналов исчерпан. Для подключения дополнительного канала необходимо докупить его в разделе "Подключение дополнительных каналов".</p>
                    <v-btn
                        color="mainNew"
                        class="white--text mt-4"
                        @click="goToUpgrade"
                    >
                        Перейти к покупке
                    </v-btn>
                </div>
                <div v-else class="connection-content">
                    <!-- QR код для подключения -->
                    <div v-if="connectionData.type === 'qr'" class="qr-section">
                        <h4 class="section-title">Отсканируйте QR-код</h4>
                        <div class="qr-wrapper">
                            <img v-if="connectionData.qrCode" :src="connectionData.qrCode" alt="QR Code" />
                            <SpinnerLoader v-else />
                        </div>
                        <div class="instructions">
                            <p><strong>Android:</strong> нажмите 3 точки ➜ Связанные устройства ➜ Привязка устройства.</p>
                            <p class="mt-2"><strong>iPhone:</strong> перейдите в Настройки WhatsApp ➜ Связанные устройства ➜ Привязка устройства.</p>
                        </div>
                    </div>

                    <!-- SMS код для подключения -->
                    <div v-else-if="connectionData.type === 'sms'" class="sms-section">
                        <h4 class="section-title">Введите код из SMS</h4>
                        <div class="sms-input-wrapper">
                            <BaseInputOutlined
                                v-model="smsCode"
                                label="Код из SMS"
                                hide-details
                                class="sms-input"
                                @input="handleSmsCodeInput"
                            />
                        </div>
                        <v-btn
                            color="mainNew"
                            class="white--text mt-4"
                            :disabled="!smsCode || smsCode.length < 4"
                            :loading="smsVerifying"
                            @click="verifySmsCode"
                        >
                            Подтвердить
                        </v-btn>
                    </div>

                    <!-- Информация о канале -->
                    <div class="channel-info-section">
                        <h4 class="section-title">Информация о канале</h4>
                        <div class="info-item">
                            <span class="info-label">Тип:</span>
                            <span class="info-value">{{ channel.type }}</span>
                        </div>
                        <div v-if="channel.phone" class="info-item">
                            <span class="info-label">Телефон:</span>
                            <span class="info-value">{{ channel.phone }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'

export default {
    name: 'ChannelConnectionSidebar',
    components: {
        SpinnerLoader,
        BaseInputOutlined,
    },
    props: {
        channel: {
            type: Object,
            default: null,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        connectionError: null,
        needsUpgrade: false,
        connectionData: {
            type: null,
            qrCode: null,
            phone: null,
        },
        smsCode: '',
        smsVerifying: false,
    }),
    watch: {
        visible(newVal) {
            if (newVal && this.channel) {
                this.loadConnectionData()
            } else {
                this.reset()
            }
        },
    },
    methods: {
        async loadConnectionData() {
            this.loading = true
            this.connectionError = null
            this.needsUpgrade = false

            try {
                // TODO: Загрузить данные подключения с API
                // Проверка лимита каналов
                const channelLimitExceeded = false // TODO: Проверить лимит

                if (channelLimitExceeded) {
                    this.needsUpgrade = true
                    this.loading = false
                    return
                }

                // Определяем тип подключения (QR или SMS)
                // Для Telegram и некоторых других каналов может быть SMS
                if (this.channel.type === 'Telegram') {
                    this.connectionData.type = 'sms'
                    // TODO: Запросить отправку SMS кода
                } else {
                    this.connectionData.type = 'qr'
                    // TODO: Загрузить QR код с API
                    // Пока используем мок
                    this.connectionData.qrCode = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UVIgQ29kZTwvdGV4dD48L3N2Zz4='
                }
            } catch (error) {
                console.error('Ошибка загрузки данных подключения:', error)
                this.connectionError = 'Не удалось загрузить данные для подключения канала'
            } finally {
                this.loading = false
            }
        },
        handleSmsCodeInput(value) {
            this.smsCode = value
        },
        async verifySmsCode() {
            this.smsVerifying = true
            try {
                // TODO: Отправить код на проверку через API
                console.log('Проверка SMS кода:', this.smsCode)
                // После успешной проверки закрываем сайдбар
                this.$store.dispatch('callNotify', 'Канал успешно подключен')
                this.close()
            } catch (error) {
                console.error('Ошибка проверки кода:', error)
                this.$store.dispatch('callNotify', 'Неверный код. Попробуйте еще раз')
            } finally {
                this.smsVerifying = false
            }
        },
        goToUpgrade() {
            // TODO: Переход на страницу покупки каналов
            this.$store.dispatch('callNotify', 'Переход на страницу покупки каналов')
        },
        close() {
            this.$emit('close')
        },
        reset() {
            this.loading = false
            this.connectionError = null
            this.needsUpgrade = false
            this.connectionData = {
                type: null,
                qrCode: null,
                phone: null,
            }
            this.smsCode = ''
            this.smsVerifying = false
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.sidebar-overlay
    position: fixed
    top: 0
    left: 0
    right: 0
    bottom: 0
    background: rgba(0, 0, 0, 0.5)
    z-index: 1000
    display: flex
    justify-content: flex-end
    align-items: stretch

.sidebar
    width: 400px
    background: white
    display: flex
    flex-direction: column
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1)

.sidebar-header
    display: flex
    justify-content: space-between
    align-items: center
    padding: 16px
    border-bottom: 1px solid #E0E0E0

.sidebar-title
    font-size: 18px
    font-weight: 500
    margin: 0

.sidebar-content
    flex: 1
    overflow-y: auto
    padding: 24px

.loading-state,
.error-state,
.upgrade-state
    text-align: center
    padding: 40px 20px

.connection-content
    display: flex
    flex-direction: column

.section-title
    font-size: 16px
    font-weight: 500
    margin-bottom: 16px

.qr-section
    margin-bottom: 24px

.qr-wrapper
    display: flex
    justify-content: center
    padding: 20px
    background: #F5F5F5
    border-radius: 8px
    margin-bottom: 16px

.qr-wrapper img
    max-width: 100%
    height: auto

.instructions
    font-size: 12px
    color: #7F8997

.sms-section
    margin-bottom: 24px

.sms-input-wrapper
    margin-bottom: 16px

.sms-input
    width: 100%

.channel-info-section
    padding-top: 24px
    border-top: 1px solid #E0E0E0

.info-item
    display: flex
    justify-content: space-between
    margin-bottom: 12px

.info-label
    font-weight: 500
    color: #7F8997

.info-value
    color: #2A3B51
</style>

